import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse, CreateInboxDto, Inbox, UpdateInboxDto } from '@/types';

/**
 * Inboxes for an account. The list is fetched whole and grouped by `bu_id` in the UI —
 * machine has no per-business-unit list endpoint, and one request beats one per unit.
 */
export const inboxesService = {
  async list(accountId: number): Promise<Inbox[]> {
    const res = await api.get<ApiResponse<{ inboxes: Inbox[] }>>(
      API_ENDPOINTS.INBOXES.BASE(accountId)
    );
    return res.data.data.inboxes;
  },

  async create(accountId: number, payload: CreateInboxDto): Promise<Inbox> {
    const res = await api.post<ApiResponse<{ inbox: Inbox }>>(
      API_ENDPOINTS.INBOXES.BASE(accountId),
      payload
    );
    return res.data.data.inbox;
  },

  /**
   * Change an inbox. `provider: null` clears its destination, and a full destination
   * replaces it — the only way back from one chosen by mistake.
   */
  async update(
    accountId: number,
    inboxId: number,
    payload: UpdateInboxDto
  ): Promise<Inbox> {
    const res = await api.patch<ApiResponse<{ inbox: Inbox }>>(
      API_ENDPOINTS.INBOXES.BY_ID(accountId, inboxId),
      payload
    );
    return res.data.data.inbox;
  },

  /**
   * Delete the inbox itself, as opposed to `clearBusinessUnit`, which only detaches it
   * from a brand. Machine answers 409 while conversations still point at it, and that
   * message carries the count — surface it rather than rewording it.
   */
  async remove(accountId: number, inboxId: number): Promise<void> {
    await api.delete(API_ENDPOINTS.INBOXES.BY_ID(accountId, inboxId));
  },

  /** Assigning is a move, not a create: machine re-points an existing mapping rather than erroring. */
  async assignBusinessUnit(accountId: number, inboxId: number, buId: number) {
    await api.put(API_ENDPOINTS.INBOXES.BUSINESS_UNIT(accountId, inboxId), {
      bu_id: buId
    });
  },

  async clearBusinessUnit(accountId: number, inboxId: number) {
    await api.delete(API_ENDPOINTS.INBOXES.BUSINESS_UNIT(accountId, inboxId));
  },

  /**
   * Adds a unit to the inbox without disturbing what it already has. If the inbox had no
   * primary yet, this unit becomes it — otherwise it lands as an extra. Already on the
   * inbox is a no-op on machine's side, so this is safe to call from a plain "add" control.
   */
  async shareWithBusinessUnit(accountId: number, inboxId: number, buId: number): Promise<Inbox> {
    const res = await api.post<ApiResponse<{ inbox: Inbox }>>(
      API_ENDPOINTS.INBOXES.BUSINESS_UNITS(accountId, inboxId),
      { bu_id: buId }
    );
    return res.data.data.inbox;
  },

  /**
   * Removes one unit from the inbox. Unlike `clearBusinessUnit`, the inbox can still belong
   * to other units afterward — machine promotes the extra added first if this was the primary.
   */
  async unshareFromBusinessUnit(
    accountId: number,
    inboxId: number,
    buId: number
  ): Promise<Inbox> {
    const res = await api.delete<ApiResponse<{ inbox: Inbox }>>(
      API_ENDPOINTS.INBOXES.BUSINESS_UNITS_BY_ID(accountId, inboxId, buId)
    );
    return res.data.data.inbox;
  },

  /**
   * Promotes a unit the inbox already has to primary. The old primary is not removed — it
   * stays on the inbox as an extra, which is what tells this apart from `assignBusinessUnit`.
   */
  async setPrimaryBusinessUnit(
    accountId: number,
    inboxId: number,
    buId: number
  ): Promise<Inbox> {
    const res = await api.put<ApiResponse<{ inbox: Inbox }>>(
      API_ENDPOINTS.INBOXES.PRIMARY_BUSINESS_UNIT(accountId, inboxId),
      { bu_id: buId }
    );
    return res.data.data.inbox;
  }
};
